import { useQuery } from '@tanstack/react-query';
import axios from 'axios';
import {
  ConvenienceDispatchFixedRouteEntity,
  ConvenienceDispatchFixedRouteDetailEntity,
  ConvenienceDispatchFixedRouteVehicleEntity,
  ConvenienceDispatchFixedRouteStopEntity,
} from 'src/entities/ConvenienceDispatchFixedRoute.entity';

const BASE_PATH = '/api/v5/convenience_dispatches/fixed_routes';

export const CONVENIENCE_DISPATCH_FIXED_ROUTES_QUERY_KEY = 'convenienceDispatchFixedRoutes';
export const CONVENIENCE_DISPATCH_FIXED_ROUTE_DETAIL_QUERY_KEY = 'convenienceDispatchFixedRouteDetail';

const deserializeOperationSnapshot = (
  data: unknown
): ConvenienceDispatchFixedRouteStopEntity['operationSnapshot'] => {
  if (data == null || typeof data !== 'object') return null;
  const snap = data as Record<string, unknown>;
  const arrival = snap.arrival_at_hhmm;
  const departure = snap.departure_at_hhmm;
  if (typeof arrival !== 'string' || typeof departure !== 'string') return null;
  return { arrivalAtHhmm: arrival, departureAtHhmm: departure };
};

const deserializeStop = (data: Record<string, unknown>): ConvenienceDispatchFixedRouteStopEntity => ({
  id: data.id as number,
  rotationNo: data.rotation_no as number,
  sequenceNo: data.sequence_no as number,
  stopType: data.stop_type as 'loading' | 'unloading',
  placeName: (data.place_name as string | null) ?? null,
  storeName: (data.store_name as string | null) ?? null,
  timeConstraintText: (data.time_constraint_text as string | null) ?? null,
  memo: (data.memo as string | null) ?? null,
  operationSnapshot: deserializeOperationSnapshot(data.operation_snapshot),
});

const deserializeDepotPair = (data: unknown):
  | { arrivalAtHhmm: string; departureAtHhmm: string }
  | null => {
  if (data == null || typeof data !== 'object') return null;
  const pair = data as Record<string, unknown>;
  const arrival = pair.arrival_at_hhmm;
  const departure = pair.departure_at_hhmm;
  if (typeof arrival !== 'string' || typeof departure !== 'string') return null;
  return { arrivalAtHhmm: arrival, departureAtHhmm: departure };
};

const deserializeVehicleOperationSnapshot = (
  data: unknown
): ConvenienceDispatchFixedRouteVehicleEntity['operationSnapshot'] => {
  if (data == null || typeof data !== 'object') return null;
  const snap = data as Record<string, unknown>;
  return {
    departure: deserializeDepotPair(snap.departure),
    arrival: deserializeDepotPair(snap.arrival),
  };
};

const deserializeVehicle = (data: Record<string, unknown>): ConvenienceDispatchFixedRouteVehicleEntity => ({
  vehicleKey: data.vehicle_key as string,
  partnerCompanyName: (data.partner_company_name as string | null) ?? null,
  truckLabel: (data.truck_label as string | null) ?? null,
  defaultDriverName: (data.default_driver_name as string | null) ?? null,
  capacityCases: (data.capacity_cases as number | null) ?? null,
  capacityQuantity: (data.capacity_quantity as number | null) ?? null,
  driverId: (data.driver_id as number | null) ?? null,
  truckId: (data.truck_id as number | null) ?? null,
  shiftStartTime: (data.shift_start_time as string | null) ?? null,
  shiftEndTime: (data.shift_end_time as string | null) ?? null,
  operationSnapshot: deserializeVehicleOperationSnapshot(data.operation_snapshot),
  sortOrder: data.sort_order as number,
  stops: ((data.stops as Record<string, unknown>[]) ?? []).map(deserializeStop),
});

const deserializeListItem = (data: Record<string, unknown>): ConvenienceDispatchFixedRouteEntity => ({
  id: data.id as number,
  name: data.name as string,
  status: data.status as 'draft' | 'active' | 'archived',
  description: (data.description as string | null) ?? null,
  vehicleCount: (data.vehicle_count as number) ?? 0,
  updatedAt: data.updated_at as string,
});

const deserializeDetail = (data: Record<string, unknown>): ConvenienceDispatchFixedRouteDetailEntity => ({
  id: data.id as number,
  name: data.name as string,
  status: data.status as 'draft' | 'active' | 'archived',
  description: (data.description as string | null) ?? null,
  vehicles: ((data.vehicles as Record<string, unknown>[]) ?? []).map(deserializeVehicle),
});

// 固定ルートの一覧・詳細は保存/名前変更/削除/上書き時のみ変化する。
// 通常の操作中は再取得不要なので、staleTime を長めに取り、window focus での再取得も停止する。
// キャッシュ破棄は mutation 側の invalidateQueries で明示的に行う。
const FIXED_ROUTE_STALE_TIME_MS = 5 * 60 * 1000;

export const useConvenienceDispatchFixedRoutesQuery = () =>
  useQuery<ConvenienceDispatchFixedRouteEntity[], Error>({
    queryKey: [CONVENIENCE_DISPATCH_FIXED_ROUTES_QUERY_KEY],
    queryFn: async () => {
      const res = await axios.get<Record<string, unknown>[]>(BASE_PATH);
      return res.data.map(deserializeListItem);
    },
    staleTime: FIXED_ROUTE_STALE_TIME_MS,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
  });

export const useConvenienceDispatchFixedRouteDetailQuery = (id: number | null) =>
  useQuery<ConvenienceDispatchFixedRouteDetailEntity, Error>({
    queryKey: [CONVENIENCE_DISPATCH_FIXED_ROUTE_DETAIL_QUERY_KEY, id],
    queryFn: async () => {
      const res = await axios.get<Record<string, unknown>>(`${BASE_PATH}/${id}`);
      return deserializeDetail(res.data);
    },
    enabled: id !== null,
    staleTime: FIXED_ROUTE_STALE_TIME_MS,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
  });
